import { AlertTriangle, ChevronRight, Loader2, MessageSquare, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { sowiftService } from "@/lib/services";
import type { SowiftAnalysisSessionResponse, SowiftProjectResponse } from "@/types";
import { GapAnalysisResults } from "./GapAnalysisResults";
import { InterviewChat } from "./InterviewChat";

export function AnalysisTab({
  projectId,
  project,
  onProjectUpdate,
}: Readonly<{
  projectId: string;
  project: SowiftProjectResponse;
  onProjectUpdate: () => Promise<void>;
}>) {
  const [sessions, setSessions] = useState<SowiftAnalysisSessionResponse[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [runningGapAnalysis, setRunningGapAnalysis] = useState(false);
  const [creatingInterview, setCreatingInterview] = useState(false);
  const [activeSession, setActiveSession] = useState<SowiftAnalysisSessionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, [projectId]);

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const response = await sowiftService.listAnalysisSessions(projectId);
      setSessions(response.data);
    } catch (err) {
      console.error("Failed to load analysis sessions:", err);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleRunGapAnalysis = async () => {
    if (project.contextItems.length === 0) {
      setError("Add context items before running analysis.");
      return;
    }
    try {
      setError(null);
      setRunningGapAnalysis(true);
      const response = await sowiftService.createAnalysisSession(projectId, {
        analysisType: "gap_analysis",
      });
      setSessions((prev) => [response.data, ...prev]);
      setActiveSession(response.data);
      await onProjectUpdate();
    } catch (err) {
      console.error("Failed to run gap analysis:", err);
      setError("Failed to run gap analysis. Please try again.");
    } finally {
      setRunningGapAnalysis(false);
    }
  };

  const handleStartInterview = async () => {
    if (project.contextItems.length === 0) {
      setError("Add context items before starting an interview.");
      return;
    }
    try {
      setError(null);
      setCreatingInterview(true);
      const response = await sowiftService.createAnalysisSession(projectId, {
        analysisType: "interview",
      });
      setSessions((prev) => [response.data, ...prev]);
      setActiveSession(response.data);
    } catch (err) {
      console.error("Failed to start interview:", err);
      setError("Failed to start interview. Please try again.");
    } finally {
      setCreatingInterview(false);
    }
  };

  // If we have an active interview session, show the chat UI
  if (activeSession?.analysisType === "interview") {
    return (
      <InterviewChat
        projectId={projectId}
        session={activeSession}
        onBack={() => {
          setActiveSession(null);
          loadSessions();
        }}
        onSessionUpdate={(updated) => setActiveSession(updated)}
      />
    );
  }

  // If we have an active gap analysis, show the results
  if (activeSession?.analysisType === "gap_analysis") {
    return <GapAnalysisResults session={activeSession} onBack={() => setActiveSession(null)} />;
  }

  if (loadingSessions) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={handleRunGapAnalysis}
          disabled={runningGapAnalysis || project.contextItems.length === 0}
          className="flex items-start gap-4 p-5 bg-surface-primary border border-border-primary hover:border-brand-primary/50 rounded-xl transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border-primary"
        >
          {runningGapAnalysis ? (
            <Loader2 className="h-8 w-8 text-brand-primary animate-spin shrink-0 mt-0.5" />
          ) : (
            <Search className="h-8 w-8 text-brand-primary shrink-0 mt-0.5" />
          )}
          <div>
            <h4 className="text-sm font-semibold text-content-primary">Gap Analysis</h4>
            <p className="text-xs text-content-tertiary mt-1">
              AI analyzes your context to identify missing information and assess SOW readiness.
            </p>
          </div>
        </button>

        <button
          onClick={handleStartInterview}
          disabled={creatingInterview || project.contextItems.length === 0}
          className="flex items-start gap-4 p-5 bg-surface-primary border border-border-primary hover:border-brand-secondary/50 rounded-xl transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border-primary"
        >
          {creatingInterview ? (
            <Loader2 className="h-8 w-8 text-brand-secondary animate-spin shrink-0 mt-0.5" />
          ) : (
            <MessageSquare className="h-8 w-8 text-brand-secondary shrink-0 mt-0.5" />
          )}
          <div>
            <h4 className="text-sm font-semibold text-content-primary">AI Interview</h4>
            <p className="text-xs text-content-tertiary mt-1">
              AI asks targeted questions to fill gaps in your project information.
            </p>
          </div>
        </button>
      </div>

      {project.contextItems.length === 0 && (
        <p className="text-xs text-content-quaternary text-center">
          Add context items in the Context tab before running analysis.
        </p>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Previous Sessions */}
      {sessions.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-content-tertiary mb-3">Previous Sessions</h3>
          <div className="space-y-2">
            {sessions.map((s) => (
              <button
                key={s.uuid}
                onClick={() => setActiveSession(s)}
                className="w-full flex items-center justify-between p-4 bg-surface-primary border border-border-primary hover:border-border-secondary rounded-xl transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {s.analysisType === "gap_analysis" ? (
                    <Search className="h-4 w-4 text-brand-primary" />
                  ) : (
                    <MessageSquare className="h-4 w-4 text-brand-secondary" />
                  )}
                  <div>
                    <h4 className="text-sm font-medium text-content-primary">
                      {s.analysisType === "gap_analysis" ? "Gap Analysis" : "AI Interview"}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className={`text-xs ${(() => {
                          if (s.status === "completed") return "text-green-400";
                          if (s.status === "running") return "text-yellow-400";
                          return "text-red-400";
                        })()}`}
                      >
                        {(() => {
                          if (s.status === "completed") return "Completed";
                          if (s.status === "running") return "In Progress";
                          return "Failed";
                        })()}
                      </span>
                      <span className="text-xs text-content-quaternary">
                        {new Date(s.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-content-quaternary" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
